<template>
    <div class="d-flex flex-column schema-table">
        <div v-if="!tableOnly" class="d-flex align-center mb-5">
            <v-text-field
                v-model="searchText"
                class="no-print"
                label="Search"
                hide-details
                dense
                color="primary"
                outlined
                prepend-inner-icon="mdi-magnify" />

            <slot name="add-button">
                <app-icon-button
                    v-if="canAdd"
                    class="ml-4 no-print"
                    color="primary"
                    icon="mdi-plus"
                    @click="add" />
            </slot>

            <schema-excel-export-button
                v-if="!disableExport"
                v-bind="$attrs"
                class="ml-4 no-print"
                :entity-key="entityKey" />
        </div>

        <v-card :class="cardClasses" :elevation="outlined ? 0 : 4">
            <component
                :is="tableComponent"
                :key="entityKey"
                ref="schemaDataTable"
                class="flex-grow-1"
                v-bind="$attrs"
                :entity-key="entityKey"
                :search-text="searchText"
                :readonly="readonly"
                :has-item-page="hasItemPage"
                @edit="edit"
                @delete="deleteItem">
                <template #extra-actions="{ item }">
                    <v-tooltip v-if="canEdit" bottom>
                        <template #activator="{ on, attrs }">
                            <v-btn
                                icon
                                :loading="savingId === item.id"
                                v-bind="attrs"
                                v-on="on"
                                @click.stop="toggleSsoExemption(item)">
                                <v-icon color="primary">
                                    {{ ssoExemptionIcon(item) }}
                                </v-icon>
                            </v-btn>
                        </template>
                        <span>{{ ssoExemptionTooltip(item) }}</span>
                    </v-tooltip>
                    <slot name="extra-actions" :item="item" />
                </template>
            </component>
        </v-card>

        <schema-form-dialog
            :entity-key="entityKey"
            :value="editedItem"
            :form="form"
            v-bind="$attrs"
            @input="onItemSaved" />
    </div>
</template>

<script>
import axios from "axios";
import tableMixin from "@/features/schemas/mixins/tableMixin";

// Users, with a toggle (admins only) for exempting a user from Shaping Minds ID: an exempt user
// can always sign in with their Daily Review Maker password, even when Shaping Minds ID is
// required.
export default {
    mixins:[tableMixin],
    types: [
        "user"
    ],
    data() {
        return {
            savingId: null
        };
    },
    methods: {
        ssoExemptionIcon(item) {
            return item.isSsoExempt ? "mdi-shield-off-outline" : "mdi-shield-account-outline";
        },
        ssoExemptionTooltip(item) {
            return item.isSsoExempt
                ? "Remove Shaping Minds ID exemption"
                : "Exempt from Shaping Minds ID";
        },
        async toggleSsoExemption(item) {
            const isSsoExempt = !item.isSsoExempt;
            const isCurrentUser = item.id === this.$store.state.authentication.user?.id;
            const change = isSsoExempt
                ? `Exempt ${item.emailAddress} from Shaping Minds ID? They'll be able to sign in `
                    + "with their Daily Review Maker password."
                : `Remove ${item.emailAddress}'s exemption from Shaping Minds ID? They'll need `
                    + "to use Shaping Minds ID if it's required.";
            // Changing it ends the user's sessions (it's in their sign in), which also cancels
            // any emailed links they haven't used.
            const consequence = isCurrentUser
                ? "You'll be signed out now, and any emailed invitation or password reset links "
                    + "you haven't used will stop working."
                : "They'll be signed out now, and any emailed invitation or password reset links "
                    + "they haven't used will stop working.";
            if (!confirm(`${change}\n\n${consequence}`)) {
                return;
            }

            this.savingId = item.id;
            try {
                await axios.put(`/api/Users/${item.id}/SsoExemption`, { isSsoExempt });
                if (isCurrentUser) {
                    window.location.assign("/login");
                    return;
                }

                const done = isSsoExempt
                    ? "Exempted from Shaping Minds ID"
                    : "Shaping Minds ID exemption removed";
                this.$store.dispatch("alert/success", done);
                await this.refresh();
            }
            finally {
                this.savingId = null;
            }
        }
    }
};
</script>
